///<reference path="../../../typings/lodash/lodash.d.ts"/>
///<reference path="processEditor-module.ts"/>
///<reference path="processInstanceService.ts"/>

module ProcessEditor {
    'use strict';
    export class Process {
        constructor(data?: IProcessData) {
            // defaults
            this.triggers = [];
            this.rootScope = new ScopeNode({ nodeId: "root", position: new Position({ top: 0, left: 0 }) });
            this.tagString = "";
            this.majorVersion = 0;
            this.minorVersion = 1;
            this.changeDescription = "";

            if (data) {
                // if data set -> init from there
                this.id = data.id;
                this.name = data.name;
                this.tagString = data.tagString;
                this.description = data.description;
                this.uniqueIdentifier = data.uniqueIdentifier;
                this.version = data.version;
                this.isNotLatestVersion = data.isNotLatestVersion;
                this.majorVersion = data.majorVersion;
                this.minorVersion = data.minorVersion;
                this.majorMinorVersions = data.majorMinorVersions;
                this.triggers = _.map(data.triggerData, (t: Trigger) => new Trigger(t));
                this.rootScope = <ScopeNode>this.deserializeNode(data.graphData);
                this.rootScope = this.convertDecisionNodesAndConnectionsToUseSeparateElseEndpoint(this.rootScope);
            }
        }

        public id: number;
        public name: string;
        public tagString: string;
        public description: string;
        public uniqueIdentifier: string;
        public version: number;
        public changeDescription: string;
        public triggers: Trigger[];
        public rootScope: ScopeNode;
        public isNotLatestVersion: boolean;
        public majorVersion: number;
        public minorVersion: number;
        public majorMinorVersions: IMajorMinorVersions[];
        public getProcessData(): IProcessData {
            return <IProcessData>{
                id: this.id,
                name: this.name,
                tagString: this.tagString,
                description: this.description,
                uniqueIdentifier: this.uniqueIdentifier,
                version: this.version,
                triggerData: this.triggers,
                graphData: this.rootScope,
                changeDescription: this.changeDescription,
                isNotLatestVersion: this.isNotLatestVersion,
                majorVersion: this.majorVersion,
                minorVersion: this.minorVersion
            };
        }

        private deserializeNode(object): Node {
            if (object && object.$type) {
                // all Nodes should have a copy constructor
                var newObj = new ProcessEditor[object.$type](object);
                for (var propKey in newObj) {
                    if (newObj.hasOwnProperty(propKey)) {
                        var prop = newObj[propKey];
                        if (_.isArray(prop)) {
                            newObj[propKey] = _.map(prop, p => this.deserializeNode(p));
                        } else {
                            newObj[propKey] = this.deserializeNode(prop);
                        }
                    }
                }
                return newObj;
            } else {
                return object;
            }
        }

        private convertDecisionNodesAndConnectionsToUseSeparateElseEndpoint(scope: ScopeNode): ScopeNode {
            // TODO: Remove once all betas have been updated

            for (let node of scope.nodes) {
                if (node instanceof StartNode) {
                    let startNode = <StartNode>node;
                    // convert old start connections to match the correct start endpoint - the connections have already been updated
                    var oldStartConnections = _.filter(scope.connections, c => c.sourceEndpointId.toLowerCase().lastIndexOf("_start") === c.sourceEndpointId.length - 6);
                    for (var oldStartConnection of oldStartConnections) {
                        console.warn(`Start node ${node.nodeId} still has an old start endpoint name, updating to latest format`);
                        oldStartConnection.sourceEndpointId = startNode.sourceEndpointDetails[0].endpointId; // start node has only one "source" endpoint
                    }
                }
                if (node instanceof ScopeNode) {
                    this.convertDecisionNodesAndConnectionsToUseSeparateElseEndpoint(node);
                    if (node.errorHandler) {
                        this.convertDecisionNodesAndConnectionsToUseSeparateElseEndpoint(node.errorHandler);
                    }
                }
                if (node instanceof DecisionNode) {
                    var decisionNode = <DecisionNode>node;

                    var elseBranchEndpointId = getEndpointId(node.nodeId, EndpointType.else);
                    var alreadyUpToDateElseBranch = _.find(scope.connections, c => c.sourceEndpointId === elseBranchEndpointId);
                    if (!alreadyUpToDateElseBranch) {
                        console.warn(`Decision node ${node.nodeId} does not have an else-branch, updating to latest format`);
                        // update connections, endpoints are already updated in the DecisionNode constructor
                        var elseBranch = _.find(decisionNode.conditionBranches, c => c.isElseBranch);
                        if (elseBranch) {
                            // this assumes the else branch does not target the same element as some other branch
                            var connToUpdate = _.find(scope.connections, c =>
                                getNodeIdFromEndpointId(c.sourceEndpointId) === decisionNode.nodeId &&
                                c.targetEndpointId === elseBranch.targetEndpointId);

                            if (connToUpdate && getEndpointTypeFromEndpointId(connToUpdate.sourceEndpointId) === EndpointType.source) {
                                connToUpdate.sourceEndpointId = elseBranchEndpointId;
                            }
                        } else {
                            console.error(<any>"No else-branch found for decision node " + node.nodeId);
                        }
                    }
                }
                if (node instanceof TaskNode && node.errorHandler) {
                    this.convertDecisionNodesAndConnectionsToUseSeparateElseEndpoint(node.errorHandler);
                }
            }

            return scope;

        }
    }

    export class Tag {
        constructor(public text: string) { }
    }

    export interface INode {
        $type: string;
        nodeId: string;
    }

    export interface INodeWithPromotableResult extends INode {
        promoteResultAs: string;
    }

    export class Node implements INode {
        constructor(public $type: string, public nodeId: string, public position: Position) {
        }
    }

    export class NodeHash {
        constructor(nodes: Node[]) {
            nodes.forEach(n => this[n.nodeId] = n);
        }
        [key: string]: Node;
    }

    export class TargetNode extends Node {
        constructor($type: string, nodeId: string, position: Position) {
            super($type, nodeId, position);
            this.targetEndpointId = getEndpointId(nodeId, EndpointType.target);
        }

        public targetEndpointId: string;
    }

    export enum EndpointType {
        source,
        target,
        else
    }

    export function getEndpointId(nodeId: string, type: EndpointType, index?: number): string {
        return `ep_${nodeId}_${EndpointType[type]}${index || ""}`;
    }

    export function getEndpointTypeFromEndpointId(endpointId: string): EndpointType {
        return EndpointType[endpointId.substring(endpointId.lastIndexOf("_") + 1)];
    }

    export function getNodeIdFromEndpointId(endpointId: string): string {
        return endpointId
            .substring(0, endpointId.lastIndexOf("_")) // strip the endpointType
            .substring(3); // strip the starting "ep_"
    }

    export function getConditionFromDecisionNode(decisionNode: DecisionNode, {sourceEndpointId, targetEndpointId}) {
        return _.find(decisionNode.conditionBranches, condition => {
            return condition.sourceEndpointId === sourceEndpointId && condition.targetEndpointId === targetEndpointId;
        });
    }

    export function generateUniqueConditionId(sourceEndpointId: string): string {
        // IDs need to be truly unique! Incremented condition counts will not work. Using unix timestamp.
        const uniqueId = moment().valueOf();
        return `decisionCondition_${sourceEndpointId}_${uniqueId}`;
    }

    export class Position {
        public top;
        public left;
        constructor(args: { top: number, left: number }) {
            this.top = args.top;
            this.left = args.left;
        }
    }

    export class SourceEndpointDetails {
        constructor(public endpointId: string, public label?: string) {
        }
    }

    export class StartNode extends Node {
        constructor(args: { nodeId: string; position: Position; startNodeType?: string }) {
            super(args.startNodeType || "StartNode", args.nodeId, args.position);
            this.sourceEndpointDetails = [new SourceEndpointDetails(getEndpointId(this.nodeId, EndpointType.source))];
        }
        public sourceEndpointDetails: SourceEndpointDetails[];
    }

    export class ForeachScopeStartNode extends StartNode {
        constructor(args: {
            nodeId: string;
            position: Position;
            iterationVariable: string;
            expression: string;
        }) {
            super({ nodeId: args.nodeId, position: args.position, startNodeType: "ForeachScopeStartNode" });
            this.iterationVariable = args.iterationVariable || "item";
            this.expression = args.expression;
        }
        public iterationVariable: string;
        public expression: string;
    }

    export class ErrorScopeStartNode extends StartNode {
        constructor(args: {
            nodeId: string;
            position: Position;
            errorVariable: string;
        }) {
            super({ startNodeType: "ErrorScopeStartNode", nodeId: args.nodeId, position: args.position });
            this.errorVariable = args.errorVariable || "error";
        }
        public errorVariable: string;
    }

    export class TaskNode extends TargetNode implements INodeWithErrorHandler, INodeWithPromotableResult {
        constructor(args: {
            nodeId: string;
            name: string;
            parameters: ITaskParameter[];
            taskName: string;
            returnType?: string;
            taskUri: string;
            position: Position;
            errorHandler?: ErrorScopeNode;
            description?: string;
            promoteResultAs?: string;
            packageName: string;
        }) {
            super("TaskNode", args.nodeId, args.position);
            this.name = args.name;
            this.parameters = args.parameters;
            this.taskName = args.taskName;
            this.returnType = args.returnType;
            this.taskUri = args.taskUri;
            this.description = args.description;
            this.promoteResultAs = args.promoteResultAs;
            this.packageName = args.packageName;

            this.sourceEndpointDetails = [new SourceEndpointDetails(getEndpointId(this.nodeId, EndpointType.source))];
            this.errorHandler = args.errorHandler;
        }
        public name: string;
        public parameters: ITaskParameter[];
        public returnType: string;
        public taskName: string;
        public taskUri: string;
        public sourceEndpointDetails: SourceEndpointDetails[];
        public errorHandler: ErrorScopeNode;
        public description: string;
        public promoteResultAs: string;
        public packageName: string;
    }

    export class DecisionNode extends TargetNode {
        constructor(args: { nodeId: string; name: string; position: Position; conditions?: ConditionExpression[]; conditionBranches?: ConditionBranch[] }) {
            super("DecisionNode", args.nodeId, args.position);
            this.name = args.name;
            this.sourceEndpointDetails = [
                new SourceEndpointDetails(getEndpointId(this.nodeId, EndpointType.source)),
                new SourceEndpointDetails(getEndpointId(this.nodeId, EndpointType.else))
            ];

            if (args.conditions) {
                // map old expressions to branches TODO: remove once all betas have been updated
                this.conditionBranches = args.conditions.map((x: ConditionExpression) => {
                    const sourceEndpointId = getEndpointId(args.nodeId, x.elseBranch ? EndpointType.else : EndpointType.source);
                    return new ConditionBranch({
                        conditionId: generateUniqueConditionId(sourceEndpointId),
                        isElseBranch: x.elseBranch,
                        expression: x.expression,
                        name: x.name,
                        sourceEndpointId: sourceEndpointId,
                        targetEndpointId: x.targetEndpointId
                    });
                });
            } else {
                this.conditionBranches = args.conditionBranches || [];
            }
        }

        public name: string;
        public conditionBranches: ConditionBranch[];
        public sourceEndpointDetails: SourceEndpointDetails[];
        public decisionSourceEndpointId: string;
    }

    export class ConditionBranch {
        constructor(args: { conditionId: string; isElseBranch: boolean; name: string; expression: string; sourceEndpointId: string; targetEndpointId: string }) {
            this.$type = "ConditionBranch";
            this.conditionId = args.conditionId;
            this.isElseBranch = args.isElseBranch;
            this.name = args.name;
            this.expression = args.expression;
            this.sourceEndpointId = args.sourceEndpointId;
            this.targetEndpointId = args.targetEndpointId;
        }

        public $type: string;
        public conditionId: string;
        public isElseBranch: boolean;
        public name: string;
        public expression: string;
        public sourceEndpointId: string;
        public targetEndpointId: string;
    }

    export class ConditionExpression {
        constructor(args: { conditionId: string; elseBranch: boolean; name: string; expression: string; sourceNodeId: string; targetNodeId: string; sourceEndpointId: string; targetEndpointId: string }) {
            this.$type = "ConditionExpression";
            this.conditionId = args.conditionId;
            this.elseBranch = args.elseBranch;
            this.name = args.name;
            this.expression = args.expression;
            this.sourceNodeId = args.sourceNodeId;
            this.targetNodeId = args.targetNodeId;
            this.sourceEndpointId = args.sourceEndpointId;
            this.targetEndpointId = args.targetEndpointId;
        }

        public $type: string;
        public conditionId: string;
        public elseBranch: boolean;
        public name: string;
        public expression: string;
        public sourceNodeId: string;
        public targetNodeId: string;
        public sourceEndpointId: string;
        public targetEndpointId: string;
    }

    export class ReturnNode extends TargetNode implements INodeWithPromotableResult {
        constructor(args: {
            nodeId: string;
            position: Position;
            returnExpression: string,
            promoteResultAs?: string
        }) {
            super("ReturnNode", args.nodeId, args.position);

            this.returnExpression = args.returnExpression || "\"\"";
            this.promoteResultAs = args.promoteResultAs;
        }

        public returnExpression: string;
        public promoteResultAs: string;s
    }

    export class ThrowErrorNode extends TargetNode {
        constructor(args: { nodeId: string, position: Position, errorMessage: string }) {
            super("ThrowErrorNode", args.nodeId, args.position);

            this.errorMessage = args.errorMessage;
        }

        public errorMessage: string;
    }

    export class ScopeNode extends Node implements INodeWithErrorHandler {
        constructor(args: IScopeNodeConstructorArgs, scopeType: string = "ScopeNode") {
            super(scopeType, args.nodeId, args.position);

            this.variables = args.variables || [];
            this.nodes = args.nodes || [this.initializeStartNode()];
            this.connections = args.connections || [];
            this.errorHandler = args.errorHandler;
            this.startEndpointId = getEndpointId(this.nodeId, EndpointType.source);
        }

        public variables: Variable[];
        public nodes: Node[];
        public connections: NodeConnection[];
        public errorHandler: ErrorScopeNode;
        public startEndpointId: string;

        /**
         * Initializes the start node if none are given
         */
        protected initializeStartNode(): StartNode {
            return new StartNode({ nodeId: this.nodeId + "_StartNode", position: new Position({ top: 100, left: 20 }) });
        }
    }

    export interface INodeWithErrorHandler {
        errorHandler: ErrorScopeNode;
    }

    export interface IScopeNodeConstructorArgs {
        nodeId: string;
        position: Position;
        variables?: Variable[];
        nodes?: Node[];
        errorHandler?: ErrorScopeNode;
        connections?: NodeConnection[];
    }

    export class ForeachScopeNode extends ScopeNode {
        constructor(args: INamedScopeNodeConstructorArgs) {
            super(args, "ForeachScopeNode");
            this.name = args.name || "Foreach";
            this.sourceEndpointDetails = [new SourceEndpointDetails(getEndpointId(this.nodeId, EndpointType.source))];
            this.targetEndpointId = getEndpointId(this.nodeId, EndpointType.target);
        }

        public name: string;
        public sourceEndpointDetails: SourceEndpointDetails[];
        public targetEndpointId: string;

        protected initializeStartNode(): StartNode {
            return new ForeachScopeStartNode(
                {
                    nodeId: this.nodeId + "_StartNode",
                    position: new Position({ top: 100, left: 20 }),
                    iterationVariable: "i",
                    expression: "new []{1,2,3}"
                });
        }
    }

    export interface INamedScopeNodeConstructorArgs extends IScopeNodeConstructorArgs {
        name: string;
    }

    export class ErrorScopeNode extends ScopeNode {
        constructor(args: INamedScopeNodeConstructorArgs) {
            super(args, "ErrorScopeNode");
            this.name = args.name;
        }
        public name: string;

        protected initializeStartNode(): StartNode {
            return new ErrorScopeStartNode(
                {
                    nodeId: this.nodeId + "_StartNode",
                    position: new Position({ top: 100, left: 20 }),
                    errorVariable: "error"
                });
        }
    }

    export class Variable {
        constructor(
            public name: string,
            public type: string,
            public value?: string) {
        }
    }

    export interface INodeConnectionKey {
        sourceEndpointId: string;
        targetEndpointId: string;
    }

    export class NodeConnection implements INodeConnectionKey {
        constructor(args: {
            sourceEndpointId: string;
            targetEndpointId: string;
        }) {
            this.$type = "NodeConnection";
            this.sourceEndpointId = args.sourceEndpointId;
            this.targetEndpointId = args.targetEndpointId;

            this.sourceNodeId = getNodeIdFromEndpointId(args.sourceEndpointId);
            if (args.targetEndpointId) {
                // apparently, targetEndpointId may be null for removed connections
                this.targetNodeId = getNodeIdFromEndpointId(args.targetEndpointId);
            }
        }

        public sourceEndpointId: string;
        public targetEndpointId: string;
        public sourceNodeId: string;
        public targetNodeId: string;
        public $type: string;

        get connectionId(): string {
            return this.sourceEndpointId + "-" + this.targetEndpointId;
        }
    }

    export interface ITaskParameter {
        name: string;
        type: string;
        inputType?: string;
        description?: string;
        minimum?: number;
        maximum?: number;
        regex?: string;
        parameterType: string;
        subParameters: ITaskParameter[];
        parameterTemplate: ITaskParameter;
        value: any;
        defaultValue: any;
        isSecret: boolean;
    }

    export class TaskParameter implements ITaskParameter {
        constructor(args: ITaskParameter) {
            this.$type = "TaskParameter";
            this.name = args.name;
            this.type = args.type;
            this.parameterType = args.parameterType;
            this.subParameters = (args.subParameters || []).map(p => new TaskParameter(p));
            this.value = args.value;
            this.defaultValue = args.defaultValue;
            this.description = args.description;
            this.isSecret = args.isSecret;
        }

        public $type: string;
        public name: string;
        public type: string;
        public parameterType: string;
        public subParameters: ITaskParameter[];
        public parameterTemplate: ITaskParameter;
        public value: any;
        public defaultValue: any;
        public description: string;
        public isSecret: boolean;
    }

    export class Trigger implements INode {
        constructor(args: { $type: string; nodeId: string; name: string; config }) {
            this.$type = args.$type;
            this.nodeId = args.nodeId;
            this.name = args.name;
            this.config = args.config;
        }
        public $type: string;
        public nodeId: string;
        public name: string;
        public config;
        public selectedTab: boolean;
    }

    export interface ITaskSelectionData {
        uri: string;
        name: string;
        packageName: string;
        returnType: string;
        arguments: ITaskParameter[];
        description: string;
    }

    export class ConditionInfo {
        constructor(
            public id: string,
            public overlay: LabelOverlay,
            public formId: string,
            public formContainerId: string,
            public sourceEndpointId: string,
            public targetEndpointId: string
        ) { }
    }
}